/**
 * FEAT-087 / S-363 — read and write the caller's timestamp format.
 *
 * The setting lives in Clerk `publicMetadata.date_format`, which is
 * readable by the client but only writable server-side — hence the split:
 * reads come straight off Clerk's user object (no round trip, no extra
 * query), writes go through `PUT /users/me/date-format`.
 *
 * Deliberately not a JWT claim. `active_agencyclient_id` is one because
 * RLS reads it on every request; this only affects rendering, so keeping
 * it out of the token avoids a Clerk JWT-template change entirely.
 */

import { useCallback, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useApi } from "./api";
import { coerceDateFormat, type DateFormatSetting } from "./date-format";

/** The caller's setting, falling back to the default until Clerk has
 * loaded (or when the user has never set one). */
export function useDateFormat(): DateFormatSetting {
  const { user } = useUser();
  return coerceDateFormat(user?.publicMetadata?.date_format);
}

export interface UseSetDateFormatResult {
  setDateFormat: (next: DateFormatSetting) => Promise<void>;
  isSaving: boolean;
  error: string | null;
}

export function useSetDateFormat(): UseSetDateFormatResult {
  const api = useApi();
  const { user } = useUser();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setDateFormat = useCallback(
    async (next: DateFormatSetting) => {
      setError(null);
      setIsSaving(true);
      try {
        await api("/users/me/date-format", {
          method: "PUT",
          body: JSON.stringify({ date_format: next }),
        });
        // The write lands in Clerk, not in our own store, so the local user
        // object is stale until it refetches — without this the just-picked
        // card would not become the selected one.
        await user?.reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not save that setting.");
      } finally {
        setIsSaving(false);
      }
    },
    [api, user]
  );

  return { setDateFormat, isSaving, error };
}
